import React, { useEffect, useState, useCallback } from 'react';
import { trips as tripsApi, extractError } from '../services/api';
import { TripStatusBadge } from '../components/ui/StatusBadge';
import { ConfirmDialog } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../store/AuthContext';
import { TripWizard } from '../components/trips/TripWizard';
import { TripDetail } from '../components/trips/TripDetail';

const STATUS_FILTERS = ['all', 'draft', 'scheduled', 'dispatched', 'in_progress', 'paused', 'completed', 'cancelled'];

function formatShortDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function TripsPage() {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [tripList, setTripList]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState('all');
  const [showWizard, setShowWizard]   = useState(false);
  const [selectedTrip, setSelected]   = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [actionLoading, setActLoading] = useState('');

  const canDispatch = hasRole(['administrator', 'fleet_manager', 'dispatcher']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const result = await tripsApi.list(params);
      setTripList(Array.isArray(result) ? result : result?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, tripId, label) => {
    setActLoading(tripId + action);
    try {
      const updated = await tripsApi[action](tripId);
      setTripList(prev => prev.map(t => t.id === tripId ? updated : t));
      if (selectedTrip?.id === tripId) setSelected(updated);
      toast.success(`Trip ${label} successfully`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActLoading('');
    }
  };

  const handleCancel = async (trip) => {
    setConfirmCancel(null);
    setActLoading(trip.id + 'cancel');
    try {
      const updated = await tripsApi.cancel(trip.id);
      setTripList(prev => prev.map(t => t.id === trip.id ? updated : t));
      toast.success('Trip cancelled');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActLoading('');
    }
  };

  const handleWizardSuccess = (newTrip) => {
    setShowWizard(false);
    setTripList(prev => [newTrip, ...prev]);
    toast.success(`Trip ${newTrip.trip_number} created successfully`);
  };

  const filtered = tripList.filter(t => {
    if (search && !t.trip_number?.toLowerCase().includes(search.toLowerCase()) &&
        !t.origin_address?.toLowerCase().includes(search.toLowerCase()) &&
        !t.destination_address?.toLowerCase().includes(search.toLowerCase()) &&
        !t.driver_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Trips</h1>
            <p className="text-sm text-gray-500">{filtered.length} trip{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          {canDispatch && (
            <button onClick={() => setShowWizard(true)} className="btn-primary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Trip
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search trips, drivers, addresses…"
              className="input-field pl-9 py-2"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {/* Status tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {STATUS_FILTERS.map(s => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="card overflow-hidden">
          {loading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <p className="text-sm font-medium">No trips found</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="table-header">Trip #</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Route</th>
                    <th className="table-header">Driver / Vehicle</th>
                    <th className="table-header">Departure</th>
                    <th className="table-header">Est. Distance</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(trip => (
                    <tr
                      key={trip.id}
                      className="hover:bg-blue-50/30 transition-colors cursor-pointer"
                      onClick={() => setSelected(trip)}
                    >
                      <td className="table-cell font-semibold text-gray-900">{trip.trip_number}</td>
                      <td className="table-cell">
                        <TripStatusBadge status={trip.status} />
                      </td>
                      <td className="table-cell max-w-xs">
                        <div className="truncate text-gray-900">{trip.origin_address?.split(',').slice(0,2).join(',')}</div>
                        <div className="truncate text-gray-500 text-xs">→ {trip.destination_address?.split(',').slice(0,2).join(',')}</div>
                      </td>
                      <td className="table-cell">
                        <div className="text-gray-900">{trip.driver_name || <span className="text-gray-400 italic">Unassigned</span>}</div>
                        <div className="text-gray-400 text-xs">{trip.unit_number ? `${trip.unit_number} ${trip.make || ''} ${trip.model || ''}` : '—'}</div>
                      </td>
                      <td className="table-cell text-gray-600">
                        <div>{formatShortDate(trip.scheduled_departure)}</div>
                        {trip.actual_departure && <div className="text-xs text-green-600">Actual: {formatShortDate(trip.actual_departure)}</div>}
                      </td>
                      <td className="table-cell">
                        {trip.estimated_distance_miles ? `${parseFloat(trip.estimated_distance_miles).toFixed(0)} mi` : '—'}
                      </td>
                      <td className="table-cell" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {/* Action buttons based on status */}
                          {trip.status === 'draft' && canDispatch && (
                            <button
                              onClick={() => handleAction('dispatch', trip.id, 'dispatched')}
                              disabled={!!actionLoading}
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors font-medium disabled:opacity-50"
                            >
                              Dispatch
                            </button>
                          )}
                          {trip.status === 'dispatched' && (
                            <button
                              onClick={() => handleAction('start', trip.id, 'started')}
                              disabled={!!actionLoading}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium disabled:opacity-50"
                            >
                              Start
                            </button>
                          )}
                          {trip.status === 'in_progress' && (
                            <>
                              <button
                                onClick={() => handleAction('pause', trip.id, 'paused')}
                                disabled={!!actionLoading}
                                className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors font-medium disabled:opacity-50"
                              >
                                Pause
                              </button>
                              <button
                                onClick={() => handleAction('complete', trip.id, 'completed')}
                                disabled={!!actionLoading}
                                className="px-2 py-1 text-xs bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors font-medium disabled:opacity-50"
                              >
                                Complete
                              </button>
                            </>
                          )}
                          {trip.status === 'paused' && (
                            <button
                              onClick={() => handleAction('resume', trip.id, 'resumed')}
                              disabled={!!actionLoading}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium disabled:opacity-50"
                            >
                              Resume
                            </button>
                          )}
                          {!['completed','cancelled'].includes(trip.status) && canDispatch && (
                            <button
                              onClick={() => setConfirmCancel(trip)}
                              disabled={!!actionLoading}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Trip Wizard Modal */}
      {showWizard && (
        <TripWizard
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />
      )}

      {/* Trip Detail Modal */}
      {selectedTrip && (
        <TripDetail
          trip={selectedTrip}
          onClose={() => setSelected(null)}
          onAction={(action, tripId) => handleAction(action, tripId, action)}
          onRefresh={load}
        />
      )}

      {/* Cancel Confirm */}
      <ConfirmDialog
        isOpen={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => handleCancel(confirmCancel)}
        title="Cancel Trip"
        message={`Are you sure you want to cancel trip ${confirmCancel?.trip_number}? This action cannot be undone.`}
        confirmText="Cancel Trip"
        variant="danger"
      />
    </div>
  );
}
